import React, {
  useEffect, useState, useCallback, useRef,
} from 'react';
import styled, { css } from 'styled-components';
import { getBrandingColor, colorTypes } from 'src/branding';

const CarouselContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
const CarouselScroll = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  width: 100%;
  overflow-y: hidden;
  overflow-x: scroll;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;
const CarouselSlide = styled.div`
  flex: 1 0 100%;
  align-items: center;
  justify-content: center;
`;
const CarouselDots = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  justify-content: center;
  padding-top: 10px;
`;
const CarouselDot = styled.div`
  display: block;
  width: 0.7em;
  height: 0.7em;
  border-radius: 50%;
  background-color: ${getBrandingColor(colorTypes.primary)};
  border: 2px solid ${getBrandingColor(colorTypes.primary)};
  margin: 0 4px;
  cursor: pointer;

  ${({ active }) => active
    && css`
      background-color: #fff;
    `}
`;

const Carousel = ({ slides }) => {
  const [slide, setSlide] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [{ touchPos, moveStart }, setTouch] = useState({
    touchPos: null,
    moveStart: null,
  });
  const ref = useRef();

  const touchstart = useCallback((e) => {
    if (!e.changedTouches.length) {
      return;
    }

    const touch = e.changedTouches[0];
    setTouch({ touchPos: touch.clientX, moveStart: Date.now() });
  }, []);

  const touchend = useCallback(
    (e) => {
      if (!e.changedTouches.length) {
        return;
      }

      const touch = e.changedTouches[0];
      setTouch({ touchPos: null, moveStart: null });
      if (touchPos && moveStart) {
        const speed = (touchPos - touch.clientX) * (Date.now() - moveStart);
        setSpeed(Math.abs(speed));
        if (speed > 0) {
          if (slide < slides.length - 1) {
            setSlide((slide) => slide + 1);
          }
        } else {
          if (slide > 0) {
            setSlide((slide) => slide - 1);
          }
        }
      }
    },
    [moveStart, slide, slides.length, touchPos],
  );

  useEffect(() => {
    const scroller = ref.current;
    scroller.addEventListener('touchstart', touchstart);
    window.addEventListener('touchend', touchend);

    return () => {
      scroller.removeEventListener('touchstart', touchstart);
      window.removeEventListener('touchend', touchend);
    };
  }, [touchend, touchstart]);

  useEffect(() => {
    let running = true;
    const then = Date.now();
    let now = Date.now();
    const animateScroll = () => {
      now = Date.now();
      const delta = now - then;
      if (ref.current && !touchPos) {
        const dest = ref.current.clientWidth * slide;
        if (ref.current.scrollLeft < dest) {
          ref.current.scrollLeft = Math.min(
            ref.current.scrollLeft + delta * speed,
            dest,
          );
        } else if (ref.current.scrollLeft > dest) {
          ref.current.scrollLeft = Math.max(
            ref.current.scrollLeft - delta * speed,
            dest,
          );
        }
      }
      if (running) {
        window.requestAnimationFrame(animateScroll);
      }
    };
    window.requestAnimationFrame(animateScroll);
    return () => {
      running = false;
    };
  }, [slide, speed, touchPos]);

  return (
    <CarouselContainer>
      <CarouselScroll ref={ref}>
        {slides.map((children, key) => (
          <CarouselSlide key={key}>{children}</CarouselSlide>
        ))}
      </CarouselScroll>
      {slides.length > 1 && (
        <CarouselDots>
          {slides.map((_, index) => (
            <CarouselDot
              active={slide === index}
              onClick={() => {
                setSpeed(1);
                setSlide(index);
              }}
              role="button"
            />
          ))}
        </CarouselDots>
      )}
    </CarouselContainer>
  );
};

export default Carousel;
